import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, memo, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useJobColors } from "../../../../hooks/useJobColors";
import { BADGE, CONTROL_HEIGHT_PX, INK, INK_BLACK, NEUTRAL, STATUS_BADGE, SURFACE } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import { CHAT_ACTION_BAR_REVEAL_SX, ChatActionBar } from "../../../UIToolkit/Chat/ChatActionBar";
import { ChatActionBarSwap } from "../../../UIToolkit/Chat/ChatActionBarSwap";
import { ChatActionButton } from "../../../UIToolkit/Chat/ChatActionButton";
import { CoverageDeltaCell } from "../../../UIToolkit/Coverage/CoverageDeltaCell";
import { ThemedSvg } from "../../../UIToolkit/ThemedSvg";
import { activateOnKey, activateOnKeyMap } from "./a11y";
import { MonthlyDayActions } from "./MonthlyDayActions";
import { MonthlyWindowRow } from "./MonthlyWindowRow";
import {
	getDayCoverage,
	getJobCountsForDay,
	getShiftWindowCountsForDay,
	MONTHLY_CELL_ROW_HEIGHT,
	type MonthlyCellDensity,
	type MonthlyDayCoverage,
	type MonthlyDayData,
	type MonthlyDayFilters,
	type MonthlyShiftWindow,
	monthlyWindowLabel,
	StaffingTotalsMode,
	selectCellWindowRows,
} from "./monthlyJobUtils";
import { useMonthlyDayActions } from "./useMonthlyDayActions";

type Props = {
	day: MonthlyDayData | undefined;
	date: DateTime;
	/** SOURCE (Sunday-indexed) coordinates of this cell's data, passed straight back to `onOpenSidebar`. */
	sourceWeekIndex: number;
	sourceDayIndex: number;
	isToday: boolean;
	isFirstOfMonth: boolean;
	isPast: boolean;
	showActual: boolean;
	/** Ring the cell when the searched staff member works this day (search highlights, never filters). */
	isHighlighted: boolean;
	visibleJobs: Job[];
	/** The department's derived shift-time windows — the same set the chip row and the legend offer. */
	windows: readonly MonthlyShiftWindow[];
	totalsMode: StaffingTotalsMode;
	filters: MonthlyDayFilters;
	/** How much fits at the measured row height — see `resolveCellDensity`. */
	density: MonthlyCellDensity;
	/** Auto-scheduler departments don't post shifts; the action layer refuses it and the icon says why. */
	isAutoScheduler: boolean;
	/**
	 * Takes the cell's coordinates rather than closing over them, so the grid can hand every cell ONE
	 * stable callback — a per-cell arrow would change identity each render and defeat the memo below.
	 */
	onOpenSidebar: (sourceWeekIndex: number, sourceDayIndex: number, date: DateTime, focusJobId?: string) => void;
	/** Leaves the month for this day's full Day view. Same rule as the day drawer's own jump. */
	onOpenDayView: (date: DateTime) => void;
};

/**
 * Floor so a cell stays legible when many rows divide the available height; cells otherwise stretch to
 * fill their row (see `MonthlyJobGrid`), which is why this is a `minHeight` and not a fixed `height`.
 */
const CELL_MIN_HEIGHT = 72;

/**
 * The date/actions header row's height — one `ChatActionButton` tall.
 *
 * Read from the shared control scale rather than re-typed as `24`: `resolveCellDensity` budgets the
 * cell's rows against this same measurement, so a literal here would let the two drift apart silently
 * the moment the micro control height moved.
 */
const CELL_HEADER_HEIGHT = CONTROL_HEIGHT_PX.MICRO;

/**
 * Action slots the top-right slot reserves: the open-day-view jump plus the three per-cell actions (post
 * shift / add staff / remove staff). The slot ALWAYS occupies this width even while its contents are
 * invisible, so revealing them on hover or focus shifts nothing.
 */
const CELL_ACTION_SLOTS = 4;
const CELL_ACTION_BAR_WIDTH = CELL_ACTION_SLOTS * CELL_HEADER_HEIGHT;

/**
 * The coverage headline's box — narrower than the action slot, and right-aligned inside it.
 *
 * Sized to the NUMBER now that nothing else is drawn in it. It was 62px while a proportional coverage
 * meter ran along the bottom edge and needed a length worth reading; with the meter gone that width was
 * just padding, and because the numeral is centred in the box it pushed the count a visible distance
 * from the people glyph that names it. 34px holds three tabular digits at 14px bold with room to spare,
 * so the glyph and its number read as one unit instead of two things sharing a corner.
 */
const CELL_HEADLINE_WIDTH = 34;

/**
 * The people glyph beside the day's amount — the same `/nurse.svg` at the same 18px the staffing day
 * header uses (`DayBlock`), so "this number is people" is signalled identically across the app.
 */
const CELL_HEADLINE_ICON_SIZE = 18;
/** Gap between that glyph and the amount box (theme spacing 0.5). */
const CELL_HEADLINE_ICON_GAP = 4;
/**
 * What the RESTING layer occupies inside the shared slot: glyph + gap + amount = 18 + 4 + 34 = 56.
 *
 * Stated as a constant, and folded into the slot width below, because the slot's whole contract is that
 * neither of its two layers can resize it. Adding the glyph widened this layer, so the slot has to be
 * sized by the WIDER of the two rather than by the action bar alone — otherwise the next glyph silently
 * overflows the corner and pushes the date row.
 */
const CELL_HEADLINE_ZONE_WIDTH = CELL_HEADLINE_ICON_SIZE + CELL_HEADLINE_ICON_GAP + CELL_HEADLINE_WIDTH;

/**
 * The shared slot's fixed width: whichever of its two layers is wider. Today that is still the action bar
 * (96 > 84), so the slot's geometry — and therefore the room left for the date and its badges on the
 * narrowest 1fr column — is byte-for-byte what it was before the glyph was added.
 */
const CELL_ACTION_ZONE_WIDTH = Math.max(CELL_ACTION_BAR_WIDTH, CELL_HEADLINE_ZONE_WIDTH);

/**
 * Keyboard shortcuts for the cell's action icons, live while the CELL itself holds focus.
 *
 * Why not simply make the icons tab stops: 42 cells x 3 icons would turn one Tab press per day into
 * four, and paging a month would mean 126 stops to walk past — the grid would become unusable by
 * keyboard, which is the opposite of accessible. So the cell stays the single tab stop for its day (as
 * it already was) and owns the actions' keyboard path; the icons carry `tabIndex={-1}` and exist for the
 * pointer. The letters are announced through `aria-keyshortcuts` on the cell and repeated in its
 * `aria-label`, so they are discoverable rather than folklore, and `activateOnKeyMap` only fires them
 * when the cell itself is the event target — never from inside a dialog one of them opened.
 */
const ADD_NURSE_KEY = "a";
const REMOVE_STAFF_KEY = "r";
const POST_SHIFT_KEY = "p";

const CountPill: FC<{ label: string; count: number; fill: string; ink: string; onClick?: () => void; ariaLabel?: string; dataTestId?: string }> = ({
	label,
	count,
	fill,
	ink,
	onClick,
	ariaLabel,
	dataTestId,
}) => {
	const activate = onClick;
	return (
		<Box
			data-testid={dataTestId}
			role={activate ? "button" : undefined}
			tabIndex={activate ? 0 : undefined}
			aria-label={activate ? ariaLabel : undefined}
			onClick={
				activate
					? (event) => {
							event.stopPropagation();
							activate();
						}
					: undefined
			}
			onKeyDown={
				activate
					? (event) => {
							event.stopPropagation();
							activateOnKey(activate)(event);
						}
					: undefined
			}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 0.5,
				// One shared row height with the shift-time rows (`MonthlyWindowRow`), rather than each mode's
				// rows sizing themselves off their own line boxes. `lineHeight: 1` below is what makes the box
				// deterministic at that height instead of leaving it to the default 1.5 leading.
				flex: "0 0 auto",
				height: `${MONTHLY_CELL_ROW_HEIGHT}px`,
				px: 0.75,
				borderRadius: `${BADGE.PILL_RADIUS}px`,
				overflow: "hidden",
				backgroundColor: alpha(fill, 0.35),
				cursor: activate ? "pointer" : "default",
				transition: "background-color 120ms ease",
				"&:hover": activate ? { backgroundColor: alpha(fill, 0.55) } : undefined,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
				<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: ink, flexShrink: 0 }} />
				<Typography
					sx={{ fontSize: "12px", fontWeight: 500, lineHeight: 1, color: INK.PRIMARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
				>
					{label}
				</Typography>
			</Box>
			<Typography sx={{ fontSize: "13px", fontWeight: 700, lineHeight: 1, color: ink, flexShrink: 0 }}>{count}</Typography>
		</Box>
	);
};

/**
 * The day's coverage state — the one thing this screen exists to show at a glance — as a fixed-size box.
 *
 * Fixed size is load-bearing rather than cosmetic: this shares the header row's right-hand slot with the
 * hover-revealed action bar, and only a box whose geometry never depends on its contents can be swapped
 * for that bar without the date row moving.
 *
 * The number is the day's SCHEDULED COUNT alone (`displayMode="filled"`), never `have/need`: schedulers
 * asked for how many people they have, not how many they need.
 *
 * And it is stated in COUNT emphasis, which is the correction to how that count used to be painted. The
 * severity presentation made the headcount itself the grade — red ink on a red field — which read as an
 * error on a tile that is only reporting how many people are on. It also stopped discriminating: the
 * day's target is the SUM of every configured slot across the whole day, so a month of ordinary days all
 * grade below the floor at once and every tile in the grid turns red together. A signal that fires on
 * all 42 cells is not a signal. Count emphasis states the headcount and nothing else — no fill, no rail,
 * no meter. The tile's job is "how many people are on this day"; how that compares to a whole day's
 * summed requirement is a question the DAY PANEL answers per shift window, where the comparison is
 * against a real per-window target rather than a figure no single day was ever meant to hit. The target
 * is still carried in the `ariaLabel`, which is the non-visual path to it and must always name both
 * counts.
 *
 * One component for both branches: `emphasis="count"` draws the number even on an ungraded day, where
 * the severity presentation renders nothing at all.
 */
const DayCoverageHeadline: FC<{ coverage: MonthlyDayCoverage; stateLabel: string; dateKey: string }> = ({ coverage, stateLabel, dateKey }) => (
	<Box sx={{ width: CELL_HEADLINE_WIDTH, height: CELL_HEADER_HEIGHT, borderRadius: 0.75, overflow: "hidden", flexShrink: 0 }}>
		<CoverageDeltaCell
			filled={coverage.filled}
			target={0}
			minimum={0}
			required={false}
			displayMode="filled"
			emphasis="count"
			// The cell's own 28px floor is TALLER than this slot, which clips; the slot's height is fixed by
			// the header row it shares with the action bar, so the floor yields rather than the row growing.
			sx={{ minHeight: CELL_HEADER_HEIGHT }}
			// Says exactly what is drawn. It used to append `/ target`, which described something the tile
			// does not show — and whose demand side was collected department-wide while the count beside it
			// respected the active filters, so a filtered tile announced a scoped number over unscoped demand.
			ariaLabel={`${stateLabel} ${coverage.filled}`}
			dataTestId={`monthly-cell-coverage-${dateKey}`}
		/>
	</Box>
);

/**
 * Memoized: the URL now updates through React Router on every page/arrow press, so the route subtree
 * re-renders. Every prop below is either a primitive or an identity the grid/state layer keeps stable
 * (`day` comes from a memoized week resolution, `filters`/`visibleJobs`/`windows`/`density` from
 * `useMemo`), so the default shallow comparison is enough — no custom comparator to drift out of sync.
 *
 * The memo does NOT cover context: `useMonthlyDayActions` subscribes each cell to `DraftModeContext` and
 * the analytics context, and `DraftModeProvider` publishes a fresh object literal as its value, so a
 * render there re-renders all 42 cells regardless of props. Every aggregation above is `useMemo`'d on
 * stable inputs, so that re-render recomputes nothing — but it is the one path that bypasses the memo,
 * and it is why nothing expensive may be added to this component's render body.
 */
export const MonthlyJobCell: FC<Props> = memo(
	({
		day,
		date,
		sourceWeekIndex,
		sourceDayIndex,
		isToday,
		isFirstOfMonth,
		isPast,
		showActual,
		isHighlighted,
		visibleJobs,
		windows,
		totalsMode,
		filters,
		density,
		isAutoScheduler,
		onOpenSidebar,
		onOpenDayView,
	}) => {
		const { t } = useTranslation();
		const jobColor = useJobColors();
		const isWindowMode = totalsMode !== StaffingTotalsMode.ByJob;
		// Read by the click guard, which tests DOM containment to tell a click on this tile apart from one
		// inside the portalled panel the day actions open.
		const cellRef = useRef<HTMLDivElement | null>(null);

		const jobCounts = useMemo(
			() => (totalsMode === StaffingTotalsMode.ByJob ? getJobCountsForDay({ day, showActual, visibleJobs, windows, filters }) : []),
			[totalsMode, day, showActual, visibleJobs, windows, filters],
		);
		const windowCounts = useMemo(
			() => (isWindowMode ? getShiftWindowCountsForDay({ day, showActual, visibleJobs, windows, filters }) : []),
			[isWindowMode, day, showActual, visibleJobs, windows, filters],
		);
		/**
		 * The day's coverage — computed in EVERY totals mode, not only the window ones.
		 *
		 * It used to be gated on `isWindowMode`, which meant `By job` — the persisted DEFAULT view — lost
		 * the one number this screen exists to show, headline and tint together. The mode chooses how the
		 * day is BROKEN DOWN below the header; it was never meant to decide whether the day has a headline.
		 */
		const coverage = useMemo(() => getDayCoverage({ day, showActual, visibleJobs, windows, filters }), [day, showActual, visibleJobs, windows, filters]);

		const windowById = useMemo(() => new Map(windows.map((window) => [window.key, window])), [windows]);
		// The BUSIEST REAL windows that fit, still drawn morning-to-night — never simply the first few of the
		// day, and never the band-less `Other` bucket, which names no time a scheduler can act on. Its people
		// stay in the day's headline total and stay listed under their exact times in the day drawer; see
		// `selectCellWindowRows`.
		const { rows: shownWindows, hiddenCount: hiddenPeopleCount } = useMemo(
			() => selectCellWindowRows(windowCounts, density.maxWindowRows),
			[windowCounts, density.maxWindowRows],
		);

		const openSidebarForJob = useCallback(
			(focusJobId?: string) => onOpenSidebar(sourceWeekIndex, sourceDayIndex, date, focusJobId),
			[onOpenSidebar, sourceWeekIndex, sourceDayIndex, date],
		);
		const openDay = useCallback(() => openSidebarForJob(), [openSidebarForJob]);

		// The shared add/remove/post layer. Past days render actuals, so they get no actions at all —
		// `showActual` short-circuits the whole zone below rather than rendering three dead icons.
		const actions = useMonthlyDayActions({ day, date, sourceWeekIndex, sourceDayIndex, showActual, isAutoScheduler, source: "cell" });
		const { canAct, canPost, openPanel } = actions;

		// The keyboard path to the same three panel tabs the icon row opens. The cell is the single tab
		// stop for the whole tile, so these shortcuts are how a keyboard reaches actions whose icons are
		// deliberately not tab stops of their own.
		const cellKeys = useMemo(
			() =>
				activateOnKeyMap({
					Enter: openDay,
					" ": openDay,
					...(showActual || !canAct
						? {}
						: {
								[ADD_NURSE_KEY]: () => openPanel("add-nurse"),
								[REMOVE_STAFF_KEY]: () => openPanel("remove-nurse"),
								// Omitted entirely where posting is refused (an auto-scheduler department), rather than
								// bound to something that quietly does nothing — and the shortcut list announced in
								// `aria-keyshortcuts` below is built from the same condition, so the two agree.
								...(canPost ? { [POST_SHIFT_KEY]: () => openPanel("create-shift") } : {}),
							}),
				}),
			[showActual, canAct, canPost, openDay, openPanel],
		);

		/**
		 * React bubbles a PORTAL's events through the React tree, so a click inside the staffing-changes
		 * panel the action icons open — mounted below as `actions.overlays` — would otherwise also open this
		 * day's sidebar behind it. DOM containment is the honest test: the panel lives under `document.body`,
		 * never inside this cell. (`activateOnKeyMap` makes the same distinction for key presses.)
		 */
		const handleCellClick = useCallback(
			(event: MouseEvent<HTMLElement>) => {
				if (event.target instanceof Node && cellRef.current?.contains(event.target)) openDay();
			},
			[openDay],
		);

		const stateLabel = showActual ? t("monthly.view.worked") : t("monthly.view.scheduled");
		/**
		 * The cell names the DAY and what it holds, then says what Enter does — it no longer opens with
		 * "Open day details" as though it were a button. It is a `gridcell` (see the root element below):
		 * a composite that holds real controls of its own, so its accessible name has to describe the day
		 * rather than impersonate one of the actions inside it.
		 */
		const cellLabel = [
			date.toFormat("MMM d, yyyy"),
			`${stateLabel} ${coverage.filled}`,
			t("monthly.view.cell.activate"),
			showActual || !canAct ? null : t(canPost ? "monthly.view.cell.shortcuts" : "monthly.view.cell.shortcuts.no.post"),
		]
			.filter(Boolean)
			.join(", ");
		const otherLabel = t("monthly.view.window.other");
		const cellShortcuts = showActual || !canAct ? undefined : [ADD_NURSE_KEY, REMOVE_STAFF_KEY, ...(canPost ? [POST_SHIFT_KEY] : [])].join(" ");

		return (
			<Box
				ref={cellRef}
				data-testid={`monthly-cell-${date.toFormat("yyyy-MM-dd")}`}
				/*
				 * `gridcell`, not `button`. The tile is one tab stop that OWNS several real controls (the
				 * open-day-view jump plus post/add/remove), which a button may not have — a button's content is
				 * presentational, so nesting them there published an accessibility tree that said one thing and
				 * behaved as another. `gridcell` is the role that legitimately holds interactive descendants;
				 * `MonthlyJobGrid` declares the `grid`/`rowgroup`/`row` structure it needs to live in.
				 */
				role="gridcell"
				tabIndex={0}
				aria-label={cellLabel}
				aria-keyshortcuts={cellShortcuts}
				onClick={handleCellClick}
				onKeyDown={cellKeys}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.25,
					p: 0.75,
					// Fill the row's share of the page height, but never collapse below legibility. `minWidth`/
					// `overflow` keep a long job name inside its column instead of widening the whole grid.
					height: "100%",
					minHeight: CELL_MIN_HEIGHT,
					minWidth: 0,
					overflow: "hidden",
					boxSizing: "border-box",
					border: `1px solid ${NEUTRAL[300]}`,
					borderRadius: 1,
					// Past days read as "actual, already happened": a muted surface distinct from live cells.
					backgroundColor: isPast ? NEUTRAL[100] : SURFACE,
					// Search highlight: ring the days the searched nurse works (an inset outline keeps the grid aligned).
					outline: isHighlighted ? `2px solid ${STATUS_BADGE.BRAND_FALLBACK}` : "none",
					outlineOffset: "-2px",
					cursor: "pointer",
					transition: "box-shadow 120ms ease",
					"&:hover": { boxShadow: `0 0 0 1px ${alpha(INK_BLACK, 0.15)}` },
					// Drives BOTH halves of the top-right swap below while the cell is hovered or holds focus:
					// the action bar in, the day's amount out. Shared with the chat action bar rather than
					// re-implemented, so there is one reveal in the app and the two halves cannot disagree.
					...CHAT_ACTION_BAR_REVEAL_SX,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, height: CELL_HEADER_HEIGHT, flex: "0 0 auto" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}>
						{/* No weekday abbreviation: the grid's sticky header already names every column. */}
						<Typography sx={{ fontSize: "14px", fontWeight: 700, color: isPast ? INK.SECONDARY : INK.PRIMARY }}>
							{isFirstOfMonth ? date.toFormat("MMM d") : date.toFormat("d")}
						</Typography>
						{isToday && (
							<Chip
								label={t("monthly.view.today.badge")}
								size="small"
								sx={{ height: 16, backgroundColor: INK_BLACK, color: SURFACE, fontSize: "9px", fontWeight: 700, "& .MuiChip-label": { px: 0.75 } }}
							/>
						)}
						{/* No "new month" badge: the first of the month already reads as `MMM d` rather than a bare
						    day number, and the divider row directly above it names the month in full. A third
						    marker for the same fact only competes with the day's own numbers for the tile's
						    narrowest row. */}
					</Box>
					{/* The shared top-right slot: the day's amount at rest, the action icons on hover/focus.
					    A tile has one spare corner and two things that want it, so they take turns inside a box of
					    FIXED width and height with both layers absolutely positioned in it — the amount is swapped
					    out, never covered, and neither state can resize the slot, so the date row cannot reflow.
					    `CELL_ACTION_ZONE_WIDTH` wide — `Math.max` of the two layers, which today is still the action
					    bar's `CELL_ACTION_SLOTS` x 24 = 96 against the amount layer's glyph + gap + amount = 84. So
					    the narrower amount layer is right-aligned inside it with the number itself staying flush to
					    the slot's right edge exactly where it was, the added glyph eats 22 of the 34px of slack
					    rather than any of the date's room, and a past day (which gets no actions) reserves exactly
					    the same geometry as a live one. `ChatActionBarSwap` plus
					    `CHAT_ACTION_BAR_REVEAL_SX` on the cell drive both halves from ONE rule, and the swap is
					    opacity-only so the amount keeps its accessible name and its text throughout.
					    On a COARSE pointer the bar is not rendered at all. Two reasons, and the second is the
					    load-bearing one: touch has no hover, so a bar pinned open there would hide the day's amount
					    permanently; and every `ChatActionButton` grows to `TOUCH_TARGET_MIN` (44px) on a coarse
					    pointer, so four of them measure 176px inside a 96px slot and spill left across the date.
					    Hiding is not a loss of function — the same actions, plus the day-view jump, sit in the day
					    drawer's header one tap away, which is and always was the touch route to them. */}
					<Box sx={{ position: "relative", width: CELL_ACTION_ZONE_WIDTH, height: CELL_HEADER_HEIGHT, flexShrink: 0 }}>
						<ChatActionBarSwap
							sx={{
								position: "absolute",
								inset: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: `${CELL_HEADLINE_ICON_GAP}px`,
							}}
						>
							{/* Names the amount as PEOPLE. `aria-hidden` because the cell's own `aria-label` already
							    says "Scheduled 18 / 20" — a second announcement would only add noise. */}
							<Box aria-hidden sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
								<ThemedSvg src="/nurse.svg" alt="" size={CELL_HEADLINE_ICON_SIZE} color="var(--inhouse-primary)" />
							</Box>
							<DayCoverageHeadline coverage={coverage} stateLabel={stateLabel} dateKey={date.toFormat("yyyy-MM-dd")} />
						</ChatActionBarSwap>
						<ChatActionBar
							dataTestId={`monthly-cell-actions-${date.toFormat("yyyy-MM-dd")}`}
							showOnCoarsePointer={false}
							sx={{
								mt: 0,
								position: "absolute",
								inset: 0,
								justifyContent: "flex-end",
								"@media (pointer: coarse)": { display: "none" },
							}}
						>
							{/* The ONE control that leaves the month. The tile's body still opens the day drawer in
							    place — a whole-tile click that navigated away would make every glance at a day cost a
							    page — so the jump is an explicit, separately labelled affordance, and it wears the
							    same glyph as the drawer's own jump because it is the same action. */}
							<ChatActionButton
								label={t("monthly.view.open.day.view")}
								icon={<EventNoteOutlinedIcon />}
								tabIndex={-1}
								onClick={(event) => {
									event.stopPropagation();
									onOpenDayView(date);
								}}
								dataTestId={`monthly-cell-open-day-view-${date.toFormat("yyyy-MM-dd")}`}
							/>
							{!showActual && <MonthlyDayActions actions={actions} focusable={false} dataTestIdPrefix={`monthly-cell-${date.toFormat("yyyy-MM-dd")}`} />}
						</ChatActionBar>
					</Box>
				</Box>

				{totalsMode === StaffingTotalsMode.ByJob &&
					jobCounts.map((job) => (
						<CountPill
							key={job.jobId}
							dataTestId={`monthly-cell-job-${job.jobId}`}
							label={job.jobName}
							count={job.count}
							fill={jobColor(job.jobId).fill}
							ink={jobColor(job.jobId).ink}
							ariaLabel={`${job.jobName} ${job.count}`}
							onClick={() => openSidebarForJob(job.jobId)}
						/>
					))}

				{isWindowMode &&
					shownWindows.map((entry) => {
						const window = windowById.get(entry.windowKey);
						if (!window) return null;
						const jobs = totalsMode === StaffingTotalsMode.JobAndShift ? entry.jobs : [];
						const label = monthlyWindowLabel(window, otherLabel);
						const description = [
							`${label} · ${t("monthly.view.scheduled.count", { count: entry.count })}`,
							...jobs.map((job) => `${job.jobName} ${job.count}`),
						].join(" · ");
						return (
							<MonthlyWindowRow
								key={entry.windowKey}
								window={window}
								otherLabel={otherLabel}
								count={entry.count}
								jobs={jobs}
								showJobNumbers={density.showJobNumbers}
								description={description}
								onClick={openDay}
								dataTestId={`monthly-cell-window-${entry.windowKey}`}
							/>
						);
					})}

				{/* The line that makes the tile add up. `hiddenCount` is PEOPLE (see `selectCellWindowRows`), so
				    the rows above plus this number always equal the headline — whether the missing people are in
				    a window that did not fit or in the band-less `Other` bucket. The accessible name spells that
				    out, because "+6" alone leans on the visual arithmetic to be understood. */}
				{isWindowMode && hiddenPeopleCount > 0 && (
					<Typography
						data-testid={`monthly-cell-more-scheduled-${date.toFormat("yyyy-MM-dd")}`}
						role="button"
						tabIndex={0}
						aria-label={t("monthly.view.more.scheduled.aria", { count: hiddenPeopleCount })}
						onClick={(event) => {
							event.stopPropagation();
							openDay();
						}}
						onKeyDown={(event) => {
							event.stopPropagation();
							activateOnKey(openDay)(event);
						}}
						sx={{ fontSize: "10px", fontWeight: 600, color: INK.SECONDARY, pl: "6px", cursor: "pointer", flex: "0 0 auto" }}
					>
						{t("monthly.view.more.scheduled", { count: hiddenPeopleCount })}
					</Typography>
				)}

				{/* The action flows' mount point, kept OUT of the hover-revealed action bar (which is
				    `pointer-events: none` at rest). Renders nothing at all until one of the actions is opened;
				    `handleCellClick` above is what keeps a click inside one of them off this cell. */}
				{!showActual && actions.overlays}
			</Box>
		);
	},
);
